<ng-container *ngIf="isLoading; else content">
  <app-loader />
</ng-container>

<ng-template #content>
  <!-- Error state -->
  <div *ngIf="hasError" class="container py-5">
    <div class="alert alert-danger text-center">
      <i class="fas fa-exclamation-triangle me-2"></i>{{ errorMessage }}
      <div class="mt-3">
        <button class="btn btn-primary" (click)="goBack()">
          <i class="fas fa-arrow-left me-2"></i>{{ 'BUTTONS.BACK' | translate }}
        </button>
      </div>
    </div>
  </div>

  <!-- Session detail -->
  <div *ngIf="!hasError && session" class="d-flex justify-content-center p-4 bg-dashboard-card">
    <app-card [customClasses]="'p-2 bg-quiz w-100'" style="max-width:900px">

      <!-- Header -->
      <!-- La durée réelle de la copie : sans elle, l'en-tête affichait un 50:00 figé (SKILL2ERA-90). -->
      <app-quiz-header [iconStart]="'fas fa-clipboard-check'" [iconEnd]="'fas fa-user-graduate'"
        [totalSeconds]="session.timeSpentSeconds" [timerAlerts]="false"
        [customClasses]="'p-3 bg-light border-bottom'">
        <div class="d-flex flex-column">
          <h2 class="fs-20 fg-600 text-dark mb-1">
            <i class="fas fa-book-open me-2 text-primary"></i>
            {{ session.testTitle }}
          </h2>
          <span class="fs-14 text-muted">
            {{ 'FOLLOWING.SESSION_RESULT_FOR' | translate }} <strong>{{ session.studentFullName }}</strong>
            &mdash; {{ session.subjectName }}
          </span>
        </div>
      </app-quiz-header>

      <!-- Pass / Fail banner -->
      <div class="alert mx-3 mt-3"
        [ngClass]="session.passed === true ? 'alert-success' : session.passed === false ? 'alert-danger' : 'alert-secondary'"
        role="alert">
        <div class="d-flex align-items-center">
          <i class="me-3 fs-2"
            [ngClass]="session.passed === true ? 'fas fa-trophy' : session.passed === false ? 'fas fa-times-circle' : 'fas fa-hourglass-half'"></i>
          <div>
            <h4 class="alert-heading mb-1">
              <ng-container *ngIf="session.passed === true">{{ 'TESTS.CONGRATULATIONS' | translate }}</ng-container>
              <ng-container *ngIf="session.passed === false">{{ 'TESTS.NEEDS_IMPROVEMENT' | translate }}</ng-container>
              <ng-container *ngIf="session.passed === null">{{ 'TESTS.IN_PROGRESS' | translate }}</ng-container>
            </h4>
            <p class="mb-0 small">
              {{ session.studentEmail }} &bull;
              {{ 'FOLLOWING.ATTEMPT' | translate }} #{{ session.attemptNumber }}
              &bull; {{ 'FOLLOWING.DURATION' | translate }}: {{ session.timeSpentSeconds | duration }}
            </p>
          </div>
        </div>
      </div>

      <!-- Score + Chart row -->
      <div class="row g-3 p-3">
        <!-- Score card -->
        <div class="col-md-6">
          <app-card [customClasses]="'p-4 h-100 border shadow-sm'">
            <div class="text-center">
              <h3 class="fs-18 fg-600 text-muted mb-3">{{ 'TESTS.YOUR_SCORE' | translate }}</h3>
              <h1 class="display-3 fg-700 mb-2" [ngClass]="getScoreColor()">
                {{ (session.percentageScore ?? session.percentage) != null ? ((session.percentageScore ?? session.percentage) | number:'1.0-1') + '%' : '—' }}
              </h1>
              <div class="d-flex justify-content-center gap-4 text-muted small">
                <div>
                  <i class="fas fa-star text-warning me-1"></i>
                  {{ session.earnedPoints ?? session.score ?? '—' }} / {{ session.totalPoints ?? '—' }}
                  {{ 'TESTS.POINTS' | translate }}
                </div>
              </div>
            </div>
          </app-card>
        </div>

        <!-- Pie chart -->
        <div class="col-md-6">
          <app-card [customClasses]="'p-4 h-100 border shadow-sm'">
            <h3 class="fs-18 fg-600 text-muted text-center mb-3">{{ 'TESTS.DISTRIBUTION' | translate }}</h3>
            <app-pie *ngIf="chartData.length > 0" [chartData]="chartData" />
            <div *ngIf="chartData.length === 0" class="text-center text-muted py-4">
              <i class="fas fa-chart-pie fs-1 mb-2"></i>
              <p class="small">{{ 'TESTS.NO_DATA' | translate }}</p>
            </div>
          </app-card>
        </div>
      </div>

      <!-- Stats row -->
      <div class="row g-3 px-3 pb-3">
        <!-- Q&A stats -->
        <div class="col-md-6">
          <app-card [customClasses]="'p-3 border shadow-sm'">
            <h6 class="text-muted mb-3"><i class="fas fa-list-ol me-2"></i>{{ 'TESTS.ANSWERS_SUMMARY' | translate }}</h6>
            <div class="d-flex flex-wrap gap-3">
              <div class="text-center">
                <div class="fs-4 fw-bold text-success">{{ session.correctAnswers ?? '—' }}</div>
                <small class="text-muted">{{ 'CHART.CORRECT_ANSWERS' | translate }}</small>
              </div>
              <div class="text-center">
                <div class="fs-4 fw-bold text-danger">{{ session.wrongAnswers ?? '—' }}</div>
                <small class="text-muted">{{ 'CHART.WRONG_ANSWERS' | translate }}</small>
              </div>
              <div class="text-center" *ngIf="session.unansweredQuestions != null">
                <div class="fs-4 fw-bold text-secondary">{{ session.unansweredQuestions }}</div>
                <small class="text-muted">{{ 'CHART.UNANSWERED' | translate }}</small>
              </div>
              <div class="text-center">
                <div class="fs-4 fw-bold text-muted">{{ session.totalQuestions ?? '—' }}</div>
                <small class="text-muted">{{ 'TESTS.TOTAL' | translate }}</small>
              </div>
            </div>
          </app-card>
        </div>

        <!-- Antifraud stats -->
        <div class="col-md-6">
          <app-card [customClasses]="'p-3 border shadow-sm'">
            <h6 class="text-muted mb-3"><i class="fas fa-shield-alt me-2"></i>{{ 'TESTS.ANTIFRAUD' | translate }}</h6>
            <div class="d-flex flex-wrap gap-3">
              <div class="text-center">
                <div class="fs-4 fw-bold" [ngClass]="session.tabSwitchCount > 2 ? 'text-danger' : 'text-secondary'">
                  {{ session.tabSwitchCount }}
                </div>
                <small class="text-muted">{{ 'TESTS.TAB_SWITCHES' | translate }}</small>
              </div>
              <div class="text-center">
                <div class="fs-4 fw-bold" [ngClass]="session.focusLossCount > 3 ? 'text-warning' : 'text-secondary'">
                  {{ session.focusLossCount }}
                </div>
                <small class="text-muted">{{ 'TESTS.FOCUS_LOSSES' | translate }}</small>
              </div>
              <div class="text-center">
                <div class="fs-4 fw-bold" [ngClass]="session.fullscreenExitCount > 1 ? 'text-warning' : 'text-secondary'">
                  {{ session.fullscreenExitCount }}
                </div>
                <small class="text-muted">{{ 'TESTS.FULLSCREEN_EXITS' | translate }}</small>
              </div>
            </div>
          </app-card>
        </div>
      </div>

      <!-- Session info -->
      <div class="px-3 pb-3">
        <app-card [customClasses]="'p-3 border shadow-sm'">
          <h6 class="text-muted mb-3"><i class="fas fa-info-circle me-2"></i>{{ 'FOLLOWING.SESSION_INFO' | translate }}</h6>
          <div class="row g-2 small">
            <div class="col-sm-4">
              <span class="text-muted">{{ 'FOLLOWING.COL_STARTED' | translate }}: </span>
              <strong>{{ session.startedAt | date:'yyyy-MM-dd HH:mm' }}</strong>
            </div>
            <div class="col-sm-4">
              <span class="text-muted">{{ 'FOLLOWING.COL_COMPLETED' | translate }}: </span>
              <strong>{{ session.completedAt ? (session.completedAt | date:'yyyy-MM-dd HH:mm') : '—' }}</strong>
            </div>
            <!-- Deux mesures, deux noms. Le temps actif ne compte que ce que
                 l'élève a passé devant sa copie ; l'écart avec le temps écoulé
                 dit une copie laissée ouverte, ou close par l'échéance. -->
            <div class="col-sm-4">
              <span class="text-muted">{{ 'FOLLOWING.ACTIVE_TIME' | translate }}: </span>
              <strong>{{ session.timeSpentSeconds | duration }}</strong>
            </div>
            <div class="col-sm-4" *ngIf="dureeEcouleeSecondes !== null">
              <span class="text-muted">{{ 'FOLLOWING.ELAPSED_TIME' | translate }}: </span>
              <strong>{{ dureeEcouleeSecondes | duration }}</strong>
            </div>
            <div class="col-sm-4">
              <span class="text-muted">{{ 'FOLLOWING.COL_STATUS' | translate }}: </span>
              <strong>{{ session.status }}</strong>
            </div>
            <div class="col-sm-4">
              <span class="text-muted">{{ 'FOLLOWING.ATTEMPT' | translate }}: </span>
              <strong>#{{ session.attemptNumber }}</strong>
            </div>
            <div class="col-sm-4">
              <span class="text-muted">{{ 'FOLLOWING.TEST_MODE' | translate }}: </span>
              <strong>{{ session.testMode }}</strong>
            </div>
          </div>
        </app-card>
      </div>

      <!-- Action buttons -->
      <div class="d-flex align-items-center justify-content-between px-3 pb-3 gap-2 flex-wrap">
        <app-back-button [route]="'/teacher/my-students'" />

        <!-- Un seul bouton là où il y en avait trois : le rapport de
             diagnostic suit la langue de l'application, il n'y a plus de
             document à demander en FR, EN ou AR.
             Proposé à qui peut l'ouvrir : l'API ne sert le rapport d'autrui
             qu'avec tests.read.all, et la vue professeur reste à écrire. -->
        <ng-container *appCan="'tests.read.all'">
          <div class="d-flex gap-2" *ngIf="session.status === 'completed'">
            <button class="btn btn-outline-primary btn-sm" (click)="openDiagnosticReport()"
              [title]="'BUTTONS.OPEN_REPORT' | translate">
              <i class="fas fa-chart-column me-1"></i>
              {{ 'BUTTONS.OPEN_REPORT' | translate }}
            </button>
          </div>
        </ng-container>
      </div>

    </app-card>
  </div>
</ng-template>
